import { TableColumn, TableSortDirection } from './table.model';

export const defaultTableSortDirection: TableSortDirection = 'asc';

export function tableFlipDirection(direction: TableSortDirection): TableSortDirection {
  return direction === 'asc' ? 'desc' : 'asc';
}

export function tableFilterBy<T extends Record<string, unknown>>(
  data: T[],
  query: string,
  columns?: TableColumn<T>[],
): T[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return data;
  }

  // Get the keys to search from columns, ensuring they exist in data items
  const searchableKeys = columns
    ? columns.map((col) => col.key as unknown as string).filter((key) => data.some((item) => key in item))
    : Object.keys(data[0] || {});

  return data.filter((item) => {
    return searchableKeys.some((key) => {
      return item[key]?.toString().toLowerCase().includes(normalizedQuery);
    });
  });
}

export function tableSortBy<T extends Record<string, unknown>, K extends keyof T>(
  data: T[],
  key: K,
  direction: TableSortDirection,
): T[] {
  return data.sort((a, b) => {
    const valA = a[key];
    const valB = b[key];

    if (valA instanceof Date && valB instanceof Date) {
      return direction === 'asc' ? valA.getTime() - valB.getTime() : valB.getTime() - valA.getTime();
    }

    const numA = parseToNumber(valA);
    const numB = parseToNumber(valB);

    if (numA !== null && numB !== null) {
      return direction === 'asc' ? numA - numB : numB - numA;
    }

    // handle string after attempting to parse to number
    if (typeof valA === 'string' && typeof valB === 'string') {
      return direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    }

    const valueAIsNullOrUndefined = valA === null || valA === undefined;
    const valueBIsNullOrUndefined = valB === null || valB === undefined;

    // Handle null/undefined values - always put them at the end
    if (valueAIsNullOrUndefined || valueBIsNullOrUndefined) {
      if (valueAIsNullOrUndefined && !valueBIsNullOrUndefined) {
        return 1;
      }
      if (!valueAIsNullOrUndefined && valueBIsNullOrUndefined) {
        return -1;
      }
    }

    return 0;
  });
}

// Extract numeric values from strings that represent numbers
export function parseToNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return value;
  }

  const stringValue = String(value).trim();

  // Only proceed if the string primarily represents a number
  // This regex matches strings that represent numeric values in various formats:
  // 1. Numbers with optional thousand separators (e.g., "1,000", "1 000", "1.000").
  // 2. Numbers with optional decimals (e.g., "123.45", "1,234.56").
  // 3. Numbers prefixed by a single non-word character (e.g., "$123", "€1,000").
  // 4. Percentages, indicated by a trailing "%" (e.g., "50%", "123.45%").
  // Note: Thousand separators can be commas, spaces, or periods, but they must be consistent within the number.
  const isNumeric = /^(?:\W?\s*)?[-+]?\d+(?:[.,\s]\d+)*(?:\.\d+)?%?$/.test(stringValue);

  if (!isNumeric) {
    return null;
  }

  // Handle percentages
  if (stringValue.endsWith('%')) {
    const numStr = stringValue.slice(0, -1).replace(/[.,\s]/g, '');
    const percentValue = parseFloat(numStr);
    return isNaN(percentValue) ? null : percentValue / 100;
  }

  // Handle regular numbers (remove any non-digit characters except decimal point and minus)
  const numStr = stringValue.replace(/[^\d.-]/g, '');
  const numericValue = parseFloat(numStr);
  return isNaN(numericValue) ? null : numericValue;
}
